import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { QrSheet } from '../components/QrSheet';
import { ErrorState, Loading } from '../components/StateBlock';
import { useAsync } from '../hooks/useAsync';
import { usePolling } from '../hooks/usePolling';
import { getAttendance, getChallenge, requestQr } from '../api';
import { paths } from '../paths';
import { AttendanceResponse, QrResponse, QrType } from '../types';

const POLL_MS = 4000;

/**
 * QR screen for check-in (/entrada) and check-out (/saida). Asks the server
 * for a QR, then polls the attendance and moves on when the manager scans it.
 */
export function QrPage({ mode }: { mode: QrType }) {
  const { eventId = '' } = useParams();
  const navigate = useNavigate();
  const { data: challenge, error: challengeError } = useAsync(
    () => getChallenge(eventId),
    [eventId]
  );

  const [qr, setQr] = useState<QrResponse | null>(null);
  const [attendance, setAttendance] = useState<AttendanceResponse | null>(null);
  const [qrError, setQrError] = useState<unknown>(null);

  const generate = useCallback(async () => {
    setQr(null);
    setQrError(null);
    try {
      setQr(await requestQr(eventId, mode));
    } catch (error) {
      // 409: the status changed (e.g. already scanned). The detail shows the right step.
      if (
        (error as { response?: { status?: number } })?.response?.status === 409
      ) {
        navigate(paths.detail(eventId), { replace: true });
        return;
      }
      setQrError(error);
    }
  }, [eventId, mode, navigate]);

  useEffect(() => {
    generate();
  }, [generate]);

  const check = useCallback(async () => {
    const a = await getAttendance(eventId);
    setAttendance(a);
    if (mode === 'CHECK_IN' && a.status === 'CHECKED_IN') {
      navigate(paths.inProgress(eventId), { replace: true });
    }
    if (mode === 'CHECK_OUT' && a.status === 'COMPLETED') {
      navigate(paths.result(eventId), { replace: true });
    }
  }, [eventId, mode, navigate]);

  useEffect(() => {
    if (qr) check().catch(() => undefined);
  }, [qr, check]);

  usePolling(check, POLL_MS, Boolean(qr));

  const close = () =>
    navigate(
      mode === 'CHECK_IN' ? paths.detail(eventId) : paths.inProgress(eventId)
    );

  if (challengeError || qrError) {
    return (
      <ErrorState
        message="Não foi possível gerar o QR Code agora."
        onRetry={generate}
      />
    );
  }
  if (!challenge) return <Loading />;

  return (
    <QrSheet
      mode={mode}
      challenge={challenge}
      qr={qr}
      attendance={attendance}
      onClose={close}
      onRegenerate={generate}
    />
  );
}
